import React, { useEffect, useState } from 'react';
import ClipLoader from 'react-spinners/ClipLoader';
import { getJurisdictions, getProjectDescription } from '@gtintel/platform-api';
import { ReportTypeEnum, ServiceType, SimpleEnquiries } from '@gtintel/platform-types';
import EditProjectSubjectModal from './EditProjectSubjectModal';
import * as S from './styles';

// If the project was created through the scope builder or API, it has one or
// more (never zero) 'subjects'. (The BE and DB call these
// 'ProjectDescriptions' which is a bad name but we're stuck with it.) This
// component loads the subjects from the BE and displays them.
//
// If the BE returns no subjects, then the project was created through the
// simple form, so we display project.description (which the user filled in as
// a free text field).
//
// To make things more confusing, the scope builder on the BE used to fill in
// the project.description field with a textual representation of all the
// subjects. As well as being pointless, there was no good way to edit this
// through the UI. The BE no longer does this, i.e. project.description will be
// blank for new projects created through the scope builder - but legacy scope
// builder project may still have a project.description that's different from
// the <Subjects>.

const Subjects = ({
  subject
}) => {
  const simpleItems = [['is_individual', 'Individual'], ['is_entity', 'Entity'], ['is_online', 'Online'], ['is_rural', 'Rural'], ['is_source_of_wealth', 'Source of Wealth'], ['social_media_review', 'Social Media Review']]
  /* eslint-disable no-unused-vars, @typescript-eslint/no-unused-vars */.filter(([attr, _label]) => subject[attr]).map(([_attr, label]) => label);
  /* eslint-enable no-unused-vars, @typescript-eslint/no-unused-vars */

  const serviceTypeLookup = Object.fromEntries(Object.entries(ServiceType).map(([k, v]) => [v, k]));
  const items = [];
  subject.name && items.push(['Name', subject.name]);
  subject.localized_name && items.push(['Local Language Name', subject.localized_name]);
  subject.jurisdiction && items.push(['Jurisdiction', subject.jurisdiction]);
  subject.address && items.push(['Address', subject.address]);
  subject.number_of_enquiries && items.push(['Enquiries', SimpleEnquiries[subject.number_of_enquiries]]);
  subject.service_type && items.push(['Service Type', serviceTypeLookup[subject.service_type]]);
  subject.dob && items.push(['Date of Birth', subject.dob]);
  subject.tax_id && items.push(['Tax ID', subject.tax_id]);
  subject.duns_number && items.push(['DUNS Number', subject.duns_number]);
  subject.phone_number && items.push(['Phone Number', subject.phone_number]);
  subject.report_type && items.push(['Report Type', ReportTypeEnum[subject.report_type]]);
  subject.email && items.push(['Email', subject.email]);
  subject.aliases && items.push(['Known Aliases', subject.aliases]);
  subject.social_media && items.push(['Social Media', subject.social_media]);
  subject.service_name && items.push(['Service Name', subject.service_name]);
  subject.category && items.push(['Category', subject.category]);
  return <div>
      {simpleItems.map((label, i) => <S.SubjectItem key={i}>
          <strong>{label}</strong>
        </S.SubjectItem>)}
      {items.map(([label, value], i) => <S.SubjectItem key={i}>
          <span>
            <strong>{label}: </strong> {value}
          </span>
        </S.SubjectItem>)}
      <S.SubjectItem key={simpleItems.length + 1}>
        <span>
          <strong>Subject Notes: </strong>{' '}
          {subject.description ? <div dangerouslySetInnerHTML={{
          __html: subject.description
        }}></div> : subject.additional_info}
        </span>
      </S.SubjectItem>
    </div>;
};
const panelHeader = subject => {
  const {
    site_name,
    service_name
  } = subject;
  return site_name || service_name || 'Service Description';
};
const ProjectDescriptionPanel = ({
  isAdmin,
  project
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [subjects, setSubjects] = useState(null);
  // index of the subect being edited in modal; or -1 if we're not editing:
  const [editedIndex, setEditedIndex] = useState(-1);
  const [jurisdictions, setJurisdictions] = useState([]);
  useEffect(() => {
    const subjectPromise = getProjectDescription({
      data: {
        limit: 100
      },
      projectId: project.id
    }).then(({
      results
    }) => {
      setSubjects(results);
    });
    const jurisdictionPromise = getJurisdictions().then(tags => setJurisdictions(tags));
    Promise.all([jurisdictionPromise, subjectPromise]).then(() => setIsLoaded(true)).catch(() => setIsLoaded('error'));
  }, [project.id]);
  if (!isLoaded) return <ClipLoader size={10} color="#777777" />;
  if (isLoaded === 'error') return <p>ERROR</p>;
  if (!subjects.length) return <p>{project.description}</p>;
  return <>
      {subjects.sort((s1, s2) => {
      const text1 = s1.service_name ?? s1.site_name ?? '';
      const text2 = s2.service_name ?? s2.site_name ?? '';
      return text1.toLowerCase().localeCompare(text2.toLowerCase());
    }).map((subject, i) => <S.SubjectContainer key={subject.id}>
            <S.SubjectTitle>{panelHeader(subject)}</S.SubjectTitle>
            <Subjects subject={subject} key={i} />
            {isAdmin && <S.SubjectEditLink onClick={() => setEditedIndex(i)}>
                Edit
              </S.SubjectEditLink>}
          </S.SubjectContainer>)}
      {isAdmin &&
    // set 'key' to force the modal's state to reset when we change subject
    <EditProjectSubjectModal key={editedIndex} hideModal={() => setEditedIndex(-1)} jurisdictions={jurisdictions} isVisible={editedIndex > -1} subject={editedIndex > -1 && subjects[editedIndex]} />}
    </>;
};
export default ProjectDescriptionPanel;